import { useEffect } from 'react';
import { usePlayerStore } from '../stores/playerStore';
import { audioEngine } from '../services/audioEngine';
import { externalListenUrl } from '../utils/streamUrl';

/**
 * Binds the audioEngine singleton to the player store and drives it from store
 * state. Safe to call from more than one component — the engine is a singleton
 * and the effects below are idempotent, so the desktop and mobile bars can both
 * use it without fighting over one audio element.
 */
export const useAudioPlayer = () => {
  const currentStation = usePlayerStore((state) => state.currentStation);
  const isPlaying = usePlayerStore((state) => state.isPlaying);
  const volume = usePlayerStore((state) => state.volume);
  const streamError = usePlayerStore((state) => state.streamError);
  const unplayableReason = usePlayerStore((state) => state.unplayableReason);
  const isBuffering = usePlayerStore((state) => state.isBuffering);
  const isConnected = usePlayerStore((state) => state.isConnected);
  const connectionQuality = usePlayerStore((state) => state.connectionQuality);

  // Engine state -> store. Registered once per consumer; the engine dedupes
  // no-op updates so this doesn't cause render loops.
  useEffect(() => {
    return audioEngine.subscribe((state) => {
      const store = usePlayerStore.getState();

      if (store.isPlaying !== state.isPlaying) store.setIsPlaying(state.isPlaying);
      if (store.isBuffering !== state.isBuffering) store.setIsBuffering(state.isBuffering);
      if (store.connectionQuality !== state.connectionQuality) {
        store.setConnectionQuality(state.connectionQuality);
      }
      if (store.isConnected !== (state.isPlaying && !state.isBuffering)) {
        store.setIsConnected(state.isPlaying && !state.isBuffering);
      }
      if (
        store.streamError !== state.streamError ||
        store.unplayableReason !== state.unplayableReason
      ) {
        store.setStreamStatus({
          streamError: state.streamError,
          unplayableReason: state.unplayableReason,
        });
      }
    });
  }, []);

  // Station changes drive a fresh load. Keyed on the uuid so unrelated re-renders
  // (a favourite toggle, a click-count refresh) don't restart the stream.
  useEffect(() => {
    if (!currentStation) return;

    const store = usePlayerStore.getState();
    audioEngine.setVolume(store.volume);

    // Respect the store's intent: a restored session, or a station picked while
    // an expired sleep timer is set, should load nothing until the user presses play.
    if (store.isPlaying) {
      audioEngine.playStation(currentStation);
    } else {
      audioEngine.prepare(currentStation);
    }
  }, [currentStation?.stationuuid]);

  // Play/pause intent from the UI.
  useEffect(() => {
    if (!currentStation) return;
    if (isPlaying) {
      audioEngine.resume();
    } else {
      audioEngine.pause();
    }
  }, [isPlaying, currentStation?.stationuuid]);

  useEffect(() => {
    audioEngine.setVolume(volume);
  }, [volume]);

  return {
    isPlaying,
    isBuffering,
    isConnected,
    connectionQuality,
    streamError,
    unplayableReason,
    /** Present when the stream can't be embedded but does work in its own tab. */
    externalUrl: currentStation ? externalListenUrl(currentStation) : null,
  };
};
